<template>
	<div class="w-100" data-cy="ecs-task-sync">
		<!-- Data Sync Section -->
		<v-card class="mb-4 w-100">
			<v-card-title class="d-flex align-center justify-space-between">
				<div class="d-flex align-center">
					<span>API Data Sync</span>
				</div>
				<!-- School Mapping Button - Only show for district level -->
				<SchoolMappingButton
					v-if="showSchoolMapping"
					:integration_type="props.integration_type"
				/>
			</v-card-title>
			<v-card-text>
				<v-row v-if="isBlackbaud">
					<v-col cols="12" md="6">
						<v-select
							v-model="selectedLevels"
							:items="levelOptions"
							item-title="title"
							item-value="value"
							label="Levels"
							multiple
							chips
							closable-chips
							variant="outlined"
							density="comfortable"
							data-cy="ecs-sync-levels-select"
						></v-select>
					</v-col>
					<v-col cols="12" md="6">
						<v-select
							v-model="selectedTerms"
							:items="termOptions"
							item-title="title"
							item-value="value"
							label="Terms"
							multiple
							chips
							closable-chips
							variant="outlined"
							density="comfortable"
							:disabled="!selectedLevels.length"
							data-cy="ecs-sync-terms-select"
						></v-select>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<v-select
							v-model="selectedEntities"
							:items="getEntityOptions"
							item-title="title"
							item-value="value"
							label="Select Entities to Sync"
							multiple
							chips
							closable-chips
							variant="outlined"
							density="comfortable"
							data-cy="ecs-sync-entities-select"
						></v-select>
					</v-col>
				</v-row>
			</v-card-text>

			<v-card-actions>
				<!-- Sync Logs/Status Button -->
				<SyncLogs :integration_type="props.integration_type" />
				<v-spacer />
				
				<v-btn
					color="primary"
					:disabled="!canSync"
					:loading="syncing"
					variant="elevated"
					data-cy="ecs-sync-selected-btn"
					@click="onStartSync"
				>
					Sync Selected Data
				</v-btn>
				<!-- Sync Progress Bar -->
			</v-card-actions>
			<v-progress-linear
				indeterminate
				color="secondary"
				v-if="syncing"
			></v-progress-linear>
		</v-card>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import SyncLogs from "@/components/school/integration/SyncLogs.vue";
import SchoolMappingButton from "@/components/school/integration/SchoolMappingButton.vue";
import { useSyncStatus } from "@/composables/useSyncStatus";
import { useSchoolStore } from "@/stores/school";
import type { BlackBaudIntegration } from "@/api_types/school";

type BlackbaudSchool = BlackBaudIntegration["schools"][number];

interface Props {
	integration_type?: string;
	hasValidConfig?: boolean;
	schools?: BlackbaudSchool[];
}

const props = withDefaults(defineProps<Props>(), {
	integration_type: "1.1",
	hasValidConfig: true,
	schools: () => [],
});

const { syncing, startSync, fetchSyncStatus } = useSyncStatus(props.integration_type);

const schoolStore = useSchoolStore();

const selectedEntities = ref<string[]>([]);
const selectedLevels = ref<number[]>([]);
const selectedTerms = ref<number[]>([]);

const isBlackbaud = computed(() => props.integration_type === "blackbaud");
const showSchoolMapping = computed(
	() =>
		schoolStore.school?.is_district == "yes" && props.integration_type !== "veracross"
);

const levelOptions = computed(() =>
	(props.schools || []).map((school) => ({
		title: school.name || `Level ${school.school_level_id}`,
		value: school.school_level_id,
	}))
);

const termOptions = computed(() => {
	if (!selectedLevels.value.length) return [];

	const terms: { title: string; value: number }[] = [];
	const seen = new Set<number>();

	for (const school of props.schools || []) {
		if (!selectedLevels.value.includes(school.school_level_id)) continue;

		const levelName = school.name || `Level ${school.school_level_id}`;
		for (const term of school.academicterms || []) {
			if (seen.has(term.term_id)) continue;
			seen.add(term.term_id);
			terms.push({
				title: `${term.description} (${term.school_year}) · ${levelName} · Academic`,
				value: term.term_id,
			});
		}
		for (const term of school.advisoryterms || []) {
			if (seen.has(term.term_id)) continue;
			seen.add(term.term_id);
			terms.push({
				title: `${term.description} (${term.school_year}) · ${levelName} · Advisory`,
				value: term.term_id,
			});
		}
	}

	return terms;
});

const canSync = computed(() => {
	if (!props.hasValidConfig || !selectedEntities.value.length || syncing.value) {
		return false;
	}
	// Require a level only once levels are available from the API
	if (isBlackbaud.value && levelOptions.value.length > 0 && !selectedLevels.value.length) {
		return false;
	}
	return true;
});

watch(selectedLevels, () => {
	const validTermIds = new Set(termOptions.value.map((t) => t.value));
	selectedTerms.value = selectedTerms.value.filter((id) => validTermIds.has(id));
});

const getEntityOptions = computed(() => {
	if (props.integration_type == "facts") {
		return [
			{ title: "Students", value: "students" },
			{ title: "Person", value: "person" },
			{ title: "Family", value: "family" },
			{ title: "Course", value: "course" },
			{ title: "Classroom", value: "classroom" },
			{ title: "Enrollment", value: "enrollment" },
		];
	} else if (props.integration_type == "nycdoe") {
		return [
			{ title: "Students", value: "students" },
			{ title: "Courses", value: "courses" },
			{ title: "Classes", value: "classes" },
			{ title: "Teachers", value: "teachers" },
			{ title: "Student Enrollment", value: "student_enrollment" },
			{ title: "Teacher Enrollment", value: "teacher_enrollment" },
		];
	} else if (props.integration_type == "veracross") {
		return [
			{ title: "Students", value: "students" },
			{ title: "Parents", value: "parents" },
			{ title: "Households", value: "households" },
			{ title: "Classes", value: "classes" },
			{ title: "Enrollments", value: "enrollments" },
			{ title: "Faculty", value: "faculty" },
			{ title: "Courses", value: "courses" },
		];
	} else if (props.integration_type == "blackbaud") {
		return [
			{ title: "Levels", value: "levels" },
			{ title: "Courses", value: "courses" },
			{ title: "Terms", value: "terms" },
			{ title: "Students", value: "students" },
			{ title: "Parents", value: "parents" },
			{ title: "Teachers", value: "teachers" },
			{ title: "Sections", value: "sections" },
			{ title: "Relationships", value: "relationships" },
			{ title: "Academic Rosters", value: "academic_rosters" },
			{ title: "Advisory Rosters", value: "advisory_rosters" },
		];
	} else {
		return [
			{ title: "Students", value: "students" },
			{ title: "Courses", value: "courses" },
			{ title: "Classes", value: "classes" },
			{ title: "Teachers", value: "teachers" },
			{ title: "Parents", value: "parents" },
			{ title: "Student Enrollment", value: "student_enrollment" },
			{ title: "Teacher Enrollment", value: "teacher_enrollment" },
			{ title: "Schools", value: "schools" },
		];
	}
});

const onStartSync = async () => {
	await startSync(selectedEntities.value, {
		levels: isBlackbaud.value ? selectedLevels.value : undefined,
		terms: isBlackbaud.value ? selectedTerms.value : undefined,
	});
};

onMounted(async () => {
	selectedEntities.value = getEntityOptions.value.map((option) => option.value);
	await fetchSyncStatus();
});
</script>

<style scoped lang="scss">
</style>
